import { forwardRef } from 'react';

interface FieldProps {
  label: React.ReactNode;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}

export function Field({ label, error, children, hint }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-ui-text-muted mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1 text-sm text-ui-text-subtle">{hint}</p>}
      {error && <p className="mt-1 text-sm text-ui-danger">{error}</p>}
    </div>
  );
}

const base = 'w-full bg-ui-bg border border-ui-border rounded-md px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent focus:ring-1 focus:ring-ui-accent/30 placeholder:text-ui-text-subtle transition-colors';

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${base} ${props.className ?? ''}`} />;
}

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea(props, ref) {
    return <textarea ref={ref} {...props} className={`${base} resize-y min-h-[80px] ${props.className ?? ''}`} />;
  },
);

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={`${base} ${props.className ?? ''}`}>
      {props.children}
    </select>
  );
}

export function Checkbox({ label, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" {...props} className="w-4 h-4 accent-ui-accent" />
      <span className="text-sm text-ui-text-muted">{label}</span>
    </label>
  );
}

export function FormGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

export function FormActions({ onCancel, saving, label = 'Guardar', formId }: { onCancel: () => void; saving: boolean; label?: string; formId?: string }) {
  return (
    <div className="px-4 sm:px-6 py-3 flex justify-end gap-3 border-t border-ui-border bg-ui-surface">
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text border border-transparent hover:border-ui-border rounded-md transition-colors"
      >
        Cancelar
      </button>
      <button
        type="submit"
        form={formId}
        disabled={saving}
        className="px-4 py-2 bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
      >
        {saving ? 'Guardando…' : label}
      </button>
    </div>
  );
}
